import type * as THREE from 'three'
import type { FileInfo } from '../loaders/file-info'
import type { GlbHierarchyNode } from '../loaders/glb'
import type { SceneBindingIndex, SelectionIndex, SelectionTarget } from '../loaders/types'
import { ImportedModelObjectMetadataService } from './ImportedModelObjectMetadataService'
import type {
  ImportedModelObjectHierarchyMetadata,
  ImportedModelObjectMetadata,
  ImportedModelSelectionTargetMetadata,
} from './ImportedModelObjectMetadataTypes'

/**
 * 插入零部件选择元数据重绑定输入。
 */
export interface RebindInsertedImportedModelAssemblySelectionMetadataInput {
  /** 本次插入到当前装配体中的 Object3D 根对象。 */
  readonly root: THREE.Object3D
  /** 插入源加载结果中的原始 hierarchy。 */
  readonly sourceHierarchy: GlbHierarchyNode
  /** 已按当前装配体路径重建后的插入 hierarchy。 */
  readonly insertedHierarchy: GlbHierarchyNode
  /** 当前装配体结果持有的选择索引，函数会就地追加插入对象。 */
  readonly selectionIndex: SelectionIndex
  /** 当前装配体结果持有的场景绑定索引，函数会就地追加插入对象。 */
  readonly sceneBindings: SceneBindingIndex
  /** 当前装配体文件信息，用于让插入对象回到宿主装配体语义空间。 */
  readonly sceneFileInfo: FileInfo
  /** 插入源文件 UUID，普通零件缺少 selectionTarget 时作为 partUuid 兜底。 */
  readonly insertedPartUuid?: string
}

/**
 * 插入零部件选择元数据重绑定结果。
 */
export interface RebindInsertedImportedModelAssemblySelectionMetadataResult {
  /** 被写入 importedModel metadata 的 Object3D 数量。 */
  readonly reboundObjectCount: number
  /** 追加或覆盖 selectionIndex 的 Object3D 数量。 */
  readonly selectionBindingCount: number
  /** 追加到 sceneBindings 的装配节点数量。 */
  readonly sceneBindingCount: number
}

//------ 装配体复制体选择元数据重绑定 开始------
/**
 * 克隆零部件选择元数据重绑定输入。
 */
export interface RebindClonedImportedModelAssemblySelectionMetadataInput {
  /** 克隆后的 Object3D 根对象。 */
  readonly root: THREE.Object3D
  /** 克隆根在当前装配体中的 occurrence ID。 */
  readonly clonedRootObjectId: string
  /** 源 Object3D UUID 到克隆 Object3D UUID 的映射。 */
  readonly clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>
  /** 当前装配体结果持有的选择索引；提供时函数会就地追加克隆对象。 */
  readonly selectionIndex?: SelectionIndex
  /** 当前装配体结果持有的场景绑定索引；提供时函数会就地追加克隆对象。 */
  readonly sceneBindings?: SceneBindingIndex
  /** 当前宿主装配体文件信息；提供时同步写入克隆对象 metadata。 */
  readonly sceneFileInfo?: FileInfo
  /** 克隆体缺少 partUuid 时使用的兜底零件或子装配 UUID。 */
  readonly insertedPartUuid?: string
}

/**
 * 克隆零部件选择元数据重绑定结果。
 */
export interface RebindClonedImportedModelAssemblySelectionMetadataResult {
  /** 被写入 importedModel metadata 的 Object3D 数量。 */
  readonly reboundObjectCount: number
  /** 追加或覆盖 selectionIndex 的 Object3D 数量。 */
  readonly selectionBindingCount: number
  /** 追加到 sceneBindings 的装配节点数量。 */
  readonly sceneBindingCount: number
}
//------ 装配体复制体选择元数据重绑定 结束------

/**
 * 插入重绑定过程中的派生运行时上下文。
 */
interface RebindRuntimeContext {
  /** 插入对象树内的 Object3D uuid 索引。 */
  readonly objectByUuid: Map<string, THREE.Object3D>
  /** 插入后 hierarchy 的 objectId 索引。 */
  readonly insertedHierarchyByObjectId: Map<string, GlbHierarchyNode>
  /** 源 hierarchy objectId 到插入后 hierarchy 节点的映射。 */
  readonly sourceObjectIdToInsertedHierarchy: Map<string, GlbHierarchyNode>
  /** 旧 assemblyNodeId 到当前装配体实例 ID 的映射。 */
  readonly newAssemblyNodeIdByOldNodeId: Map<string, string>
  /** 本次插入根组件的装配实例 ID。 */
  readonly rootAssemblyNodeId: string
  /** 本次插入根组件的选择代理对象。 */
  readonly rootProxyObject: THREE.Object3D
  /** 当前宿主装配体文件信息。 */
  readonly sceneFileInfo: FileInfo
  /** 插入源文件 UUID 兜底值。 */
  readonly insertedPartUuid: string
}

/**
 * 克隆重绑定运行时上下文。
 *
 * 设计说明：
 * 引用复制和同源装配体插入会 clone 现有 Object3D 树，旧 metadata 中的
 * assemblyNodeId 仍指向源实例。这里统一保存源对象到克隆对象的映射，
 * 让后续选择、配合和移动命令都只落在当前克隆 occurrence 内。
 */
interface ClonedRebindRuntimeContext {
  /** 克隆对象树内的 Object3D uuid 索引。*/
  readonly objectByUuid: Map<string, THREE.Object3D>
  /** 源 Object3D uuid 到克隆 Object3D uuid 的映射。*/
  readonly clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>
  /** 克隆根 occurrence ID。*/
  readonly clonedRootObjectId: string
  /** 克隆根 occurrence 对应的选择代理对象。*/
  readonly rootProxyObject: THREE.Object3D
  /** 克隆对象缺少零件 UUID 时使用的兜底值。*/
  readonly insertedPartUuid: string
}

/**
 * 插入重绑定过程中沿 Object3D 父子链继承的组件选择语义。
 */
interface ReboundSelectionContext {
  /** 当前对象应归属的装配节点 ID。 */
  readonly assemblyNodeId: string
  /** 当前对象应归属的零件或子装配文件 UUID。 */
  readonly partUuid: string
  /** 当前装配节点对应的选择代理对象。 */
  readonly proxyObject: THREE.Object3D
}

/**
 * 规范化字符串字段，避免空白值进入运行时索引。
 *
 * @param value 待规范化的未知值。
 * @returns 去除首尾空白后的字符串；无效值返回空字符串。
 */
function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 克隆可选选择目标中的子实体字段。
 *
 * @param target 源选择目标。
 * @returns 可写入 selectionIndex 的稳定选择目标。
 */
function cloneSelectionTargetSubEntityFields(
  target: ImportedModelSelectionTargetMetadata | undefined,
): Partial<SelectionTarget> {
  return {
    ...(normalizeString(target?.primitiveId)
      ? { primitiveId: normalizeString(target?.primitiveId) }
      : {}),
    ...(normalizeString(target?.stepFaceId)
      ? { stepFaceId: normalizeString(target?.stepFaceId) }
      : {}),
  }
}

/**
 * 收集 Object3D 索引，避免插入提交时在每个对象上重复遍历子树。
 *
 * @param root 插入对象根。
 * @returns uuid 到 Object3D 的索引。
 */
function collectObjectByUuid(root: THREE.Object3D): Map<string, THREE.Object3D> {
  const objectByUuid = new Map<string, THREE.Object3D>()
  root.traverse((object) => {
    objectByUuid.set(object.uuid, object)
  })
  return objectByUuid
}

/**
 * 收集插入后 hierarchy 节点索引。
 *
 * @param node 当前 hierarchy 节点。
 * @param target 输出索引。
 */
function collectHierarchyByObjectId(
  node: GlbHierarchyNode,
  target: Map<string, GlbHierarchyNode>,
): void {
  target.set(node.objectId, node)
  node.children.forEach((child) => collectHierarchyByObjectId(child, target))
}

/**
 * 按源 hierarchy 与插入 hierarchy 的递归位置建立节点映射。
 *
 * 设计说明：
 * 插入链路移动的是同一棵 Object3D，通常 objectId 不变；这里仍按节点位置建立映射，
 * 是为了让后续 hierarchy 生成策略变化时，选择语义仍能从源节点稳定过渡到插入节点。
 *
 * @param sourceNode 源 hierarchy 节点。
 * @param insertedNode 插入后 hierarchy 节点。
 * @param target 源 objectId 到插入节点的输出索引。
 */
function collectSourceObjectIdToInsertedHierarchy(
  sourceNode: GlbHierarchyNode,
  insertedNode: GlbHierarchyNode,
  target: Map<string, GlbHierarchyNode>,
): void {
  target.set(sourceNode.objectId, insertedNode)
  const childCount = Math.min(sourceNode.children.length, insertedNode.children.length)
  for (let childIndex = 0; childIndex < childCount; childIndex += 1) {
    const sourceChild = sourceNode.children[childIndex]
    const insertedChild = insertedNode.children[childIndex]
    if (sourceChild && insertedChild) {
      collectSourceObjectIdToInsertedHierarchy(sourceChild, insertedChild, target)
    }
  }
}

/**
 * 解析对象当前声明的装配节点 ID。
 *
 * @param metadata 对象导入元数据。
 * @returns 装配节点 ID；缺失时返回空字符串。
 */
function resolveOriginalAssemblyNodeId(
  metadata: ImportedModelObjectMetadata | undefined,
): string {
  return normalizeString(metadata?.selectionTarget?.assemblyNodeId)
    || normalizeString(metadata?.assemblyNodeId)
}

/**
 * 判断代理对象 ID 是否仍属于当前插入对象树。
 *
 * @param proxyObjectId 代理 Object3D uuid。
 * @param objectByUuid 插入对象索引。
 * @returns 是否为有效代理 ID。
 */
function hasValidProxyObjectId(
  proxyObjectId: string,
  objectByUuid: Map<string, THREE.Object3D>,
): boolean {
  return proxyObjectId.length > 0 && objectByUuid.has(proxyObjectId)
}

/**
 * 基于源元数据建立旧 assemblyNodeId 到当前装配体实例 ID 的映射。
 *
 * @param root 插入对象根。
 * @param context 运行时上下文。
 * @returns 旧装配节点 ID 到新装配节点 ID 的映射。
 */
function buildAssemblyNodeRebindMap(
  root: THREE.Object3D,
  context: Pick<RebindRuntimeContext, 'objectByUuid' | 'insertedHierarchyByObjectId' | 'rootAssemblyNodeId'>,
): Map<string, string> {
  const newAssemblyNodeIdByOldNodeId = new Map<string, string>()
  root.traverse((object) => {
    const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(object)
    const originalAssemblyNodeId = resolveOriginalAssemblyNodeId(metadata)
    if (!originalAssemblyNodeId || newAssemblyNodeIdByOldNodeId.has(originalAssemblyNodeId)) {
      return
    }

    const proxyObjectId = normalizeString(metadata?.selectionProxyObjectId)
    if (hasValidProxyObjectId(proxyObjectId, context.objectByUuid)) {
      newAssemblyNodeIdByOldNodeId.set(originalAssemblyNodeId, proxyObjectId)
      return
    }

    if (context.insertedHierarchyByObjectId.has(object.uuid)) {
      newAssemblyNodeIdByOldNodeId.set(originalAssemblyNodeId, object.uuid)
      return
    }

    newAssemblyNodeIdByOldNodeId.set(originalAssemblyNodeId, context.rootAssemblyNodeId)
  })
  return newAssemblyNodeIdByOldNodeId
}

/**
 * 创建 hierarchy metadata 镜像。
 *
 * @param node 插入后的 hierarchy 节点。
 * @param sceneFileInfo 当前装配体文件信息。
 * @returns 可写入 Object3D 的 hierarchy metadata。
 */
function createHierarchyMetadata(
  node: GlbHierarchyNode,
  sceneFileInfo: FileInfo,
): ImportedModelObjectHierarchyMetadata {
  return {
    fileId: sceneFileInfo.fileId,
    name: node.name,
    path: node.path,
    nodeType: node.nodeType,
    selectable: node.selectable,
    parentObjectId: node.parentObjectId,
    depth: node.depth,
    meta: node.meta,
  }
}

/**
 * 解析当前对象对应的插入后 hierarchy 节点。
 *
 * @param object 当前对象。
 * @param context 运行时上下文。
 * @returns 插入后 hierarchy 节点；未命中时返回 undefined。
 */
function resolveInsertedHierarchyNode(
  object: THREE.Object3D,
  context: RebindRuntimeContext,
): GlbHierarchyNode | undefined {
  return context.insertedHierarchyByObjectId.get(object.uuid)
    ?? context.sourceObjectIdToInsertedHierarchy.get(object.uuid)
}

/**
 * 判断 hierarchy 节点是否代表装配配合与移动命令可操作的组件边界。
 *
 * 设计说明：
 * 1. 普通零件内部 primitive/mesh 不能各自成为组件，否则会破坏零件整体移动；
 * 2. 插入装配体的子级 part/assembly 必须成为新的组件边界，否则 Mate 会把内部零件吸收到插入根；
 * 3. 因此这里只把 part、assembly 和带外部文件身份的节点作为可继承边界。
 *
 * @param hierarchyNode 插入后的 hierarchy 节点。
 * @returns 是否为组件选择边界。
 */
function isAssemblyComponentSelectionBoundary(
  hierarchyNode: GlbHierarchyNode | undefined,
): boolean {
  if (!hierarchyNode) {
    return false
  }
  if (!hierarchyNode.meta) {
    return true
  }

  const semanticKind = normalizeString(hierarchyNode?.meta?.semanticKind)
  return semanticKind === 'part'
    || semanticKind === 'assembly'
    || Boolean(normalizeString(hierarchyNode?.meta?.externalFileUuid))
}

/**
 * 解析对象在当前装配体中的新 assemblyNodeId。
 *
 * @param object 当前对象。
 * @param metadata 当前对象导入元数据。
 * @param context 运行时上下文。
 * @returns 当前装配体实例级 assemblyNodeId。
 */
/**
 * 判断克隆对象携带的 hierarchy metadata 是否是组件选择边界。
 *
 * @param hierarchy 克隆对象自身携带的导入层级镜像。
 * @returns 是否应重新开启一个组件级 selection context。
 */
function isImportedHierarchyMetadataSelectionBoundary(
  hierarchy: ImportedModelObjectHierarchyMetadata | undefined,
): boolean {
  if (!hierarchy) {
    return false
  }
  if (!hierarchy.meta) {
    return true
  }

  const semanticKind = normalizeString(hierarchy.meta.semanticKind)
  return semanticKind === 'part'
    || semanticKind === 'assembly'
    || Boolean(normalizeString(hierarchy.meta.externalFileUuid))
}

/**
 * 解析对象在当前装配体中的新 assemblyNodeId。
 *
 * @param object 当前对象。
 * @param metadata 当前对象导入元数据。
 * @param context 插入重绑定上下文。
 * @returns 当前装配体实例级 assemblyNodeId。
 */
function resolveReboundAssemblyNodeId(
  object: THREE.Object3D,
  metadata: ImportedModelObjectMetadata | undefined,
  context: RebindRuntimeContext,
): string {
  const originalAssemblyNodeId = resolveOriginalAssemblyNodeId(metadata)
  const mappedAssemblyNodeId = originalAssemblyNodeId
    ? context.newAssemblyNodeIdByOldNodeId.get(originalAssemblyNodeId)
    : undefined
  if (mappedAssemblyNodeId) {
    return mappedAssemblyNodeId
  }

  const proxyObjectId = normalizeString(metadata?.selectionProxyObjectId)
  if (hasValidProxyObjectId(proxyObjectId, context.objectByUuid)) {
    return proxyObjectId
  }

  if (object === context.rootProxyObject) {
    return context.rootAssemblyNodeId
  }

  return context.rootAssemblyNodeId
}

/**
 * 解析当前对象应写入的选择代理对象。
 *
 * @param metadata 当前对象导入元数据。
 * @param assemblyNodeId 当前装配体实例级 assemblyNodeId。
 * @param context 运行时上下文。
 * @returns 选择代理 Object3D。
 */
function resolveSelectionProxyObject(
  metadata: ImportedModelObjectMetadata | undefined,
  assemblyNodeId: string,
  context: RebindRuntimeContext,
): THREE.Object3D {
  const proxyObjectId = normalizeString(metadata?.selectionProxyObjectId)
  const originalProxyObject = context.objectByUuid.get(proxyObjectId)
  if (originalProxyObject) {
    return originalProxyObject
  }

  return context.objectByUuid.get(assemblyNodeId) ?? context.rootProxyObject
}

/**
 * 解析组件边界自身的选择上下文。
 *
 * @param object 当前对象。
 * @param metadata 当前对象导入元数据。
 * @param hierarchyNode 插入后的 hierarchy 节点。
 * @param context 运行时上下文。
 * @returns 当前边界的选择上下文。
 */
function resolveOwnReboundSelectionContext(
  object: THREE.Object3D,
  metadata: ImportedModelObjectMetadata | undefined,
  hierarchyNode: GlbHierarchyNode | undefined,
  context: RebindRuntimeContext,
): ReboundSelectionContext {
  const hierarchyAssemblyNodeId = isAssemblyComponentSelectionBoundary(hierarchyNode)
    ? normalizeString(hierarchyNode?.objectId)
    : ''
  const assemblyNodeId = hierarchyAssemblyNodeId
    || resolveReboundAssemblyNodeId(object, metadata, context)
  const proxyObject = context.objectByUuid.get(assemblyNodeId)
    ?? resolveSelectionProxyObject(metadata, assemblyNodeId, context)
  const partUuid = resolvePartUuid(metadata, hierarchyNode, context)
  return {
    assemblyNodeId,
    partUuid,
    proxyObject,
  }
}

/**
 * 解析当前对象最终应写入的选择上下文。
 *
 * @param object 当前对象。
 * @param metadata 当前对象导入元数据。
 * @param hierarchyNode 插入后的 hierarchy 节点。
 * @param inheritedSelectionContext 父级组件边界传入的选择上下文。
 * @param context 运行时上下文。
 * @returns 当前对象的选择上下文。
 */
function resolveReboundSelectionContext(input: {
  readonly object: THREE.Object3D
  readonly metadata: ImportedModelObjectMetadata | undefined
  readonly hierarchyNode: GlbHierarchyNode | undefined
  readonly inheritedSelectionContext: ReboundSelectionContext | null
  readonly context: RebindRuntimeContext
}): ReboundSelectionContext {
  if (isAssemblyComponentSelectionBoundary(input.hierarchyNode) || !input.inheritedSelectionContext) {
    return resolveOwnReboundSelectionContext(
      input.object,
      input.metadata,
      input.hierarchyNode,
      input.context,
    )
  }

  return input.inheritedSelectionContext
}

/**
 * 解析当前对象的零件 UUID。
 *
 * @param metadata 当前对象导入元数据。
 * @param hierarchyNode 插入后 hierarchy 节点。
 * @param context 运行时上下文。
 * @returns 零件 UUID；缺失时返回空字符串。
 */
function resolvePartUuid(
  metadata: ImportedModelObjectMetadata | undefined,
  hierarchyNode: GlbHierarchyNode | undefined,
  context: RebindRuntimeContext,
): string {
  return normalizeString(metadata?.selectionTarget?.partUuid)
    || normalizeString(metadata?.partFileUuid)
    || normalizeString(hierarchyNode?.meta?.externalFileUuid)
    || context.insertedPartUuid
}

/**
 * 创建当前装配体实例级选择目标。
 *
 * @param input 创建输入。
 * @returns 可写入 metadata 和 selectionIndex 的选择目标。
 */
function createReboundSelectionTarget(input: {
  readonly metadata: ImportedModelObjectMetadata | undefined
  readonly assemblyNodeId: string
  readonly partUuid: string
}): SelectionTarget {
  return {
    assemblyNodeId: input.assemblyNodeId,
    ...(input.partUuid ? { partUuid: input.partUuid } : {}),
    ...cloneSelectionTargetSubEntityFields(input.metadata?.selectionTarget),
  }
}

/**
 * 重写 bodyBindingId 级选择目标。
 *
 * @param metadata 当前对象导入元数据。
 * @param fallbackAssemblyNodeId 当前对象的装配节点 ID。
 * @param fallbackPartUuid 当前对象的零件 UUID。
 * @param context 运行时上下文。
 * @returns 重写后的 body 绑定选择目标；无有效条目时返回 undefined。
 */
function rebindBodyBindingSelectionTargetsById(
  metadata: ImportedModelObjectMetadata | undefined,
  fallbackAssemblyNodeId: string,
  fallbackPartUuid: string,
  context: RebindRuntimeContext,
): Record<string, ImportedModelSelectionTargetMetadata> | undefined {
  const sourceTargets = metadata?.bodyBindingSelectionTargetsById
  if (!sourceTargets) {
    return undefined
  }

  const reboundTargets: Record<string, ImportedModelSelectionTargetMetadata> = {}
  Object.entries(sourceTargets).forEach(([bodyBindingId, target]) => {
    const normalizedBodyBindingId = normalizeString(bodyBindingId)
    if (!normalizedBodyBindingId) {
      return
    }

    const originalAssemblyNodeId = normalizeString(target?.assemblyNodeId)
    const assemblyNodeId = context.newAssemblyNodeIdByOldNodeId.get(originalAssemblyNodeId)
      ?? (context.objectByUuid.has(originalAssemblyNodeId) ? originalAssemblyNodeId : fallbackAssemblyNodeId)
    if (!assemblyNodeId) {
      return
    }

    const partUuid = normalizeString(target?.partUuid) || fallbackPartUuid
    reboundTargets[normalizedBodyBindingId] = {
      assemblyNodeId,
      ...(partUuid ? { partUuid } : {}),
      ...cloneSelectionTargetSubEntityFields(target),
    }
  })

  return Object.keys(reboundTargets).length > 0 ? reboundTargets : undefined
}

/**
 * 向 sceneBindings 追加唯一代理对象。
 *
 * @param sceneBindings 当前装配体场景绑定索引。
 * @param assemblyNodeId 当前装配体实例级 assemblyNodeId。
 * @param proxyObject 当前实例选择代理对象。
 * @returns 是否新增了 assemblyNodeId 绑定关系。
 */
function appendSceneBinding(
  sceneBindings: SceneBindingIndex,
  assemblyNodeId: string,
  proxyObject: THREE.Object3D,
): boolean {
  sceneBindings.byObject3DUuid[proxyObject.uuid] = proxyObject

  const bindings = sceneBindings.byAssemblyNodeId[assemblyNodeId] ?? []
  if (bindings.includes(proxyObject.uuid)) {
    sceneBindings.byAssemblyNodeId[assemblyNodeId] = bindings
    return false
  }

  sceneBindings.byAssemblyNodeId[assemblyNodeId] = [...bindings, proxyObject.uuid]
  return bindings.length === 0
}

/**
 * 判断对象是否需要参与插入重绑定。
 *
 * @param object 当前对象。
 * @param hierarchyNode 插入后 hierarchy 节点。
 * @param metadata 当前对象导入元数据。
 * @param root 插入对象根。
 * @returns 是否需要写入导入模型装配选择语义。
 */
function shouldRebindObject(
  object: THREE.Object3D,
  hierarchyNode: GlbHierarchyNode | undefined,
  metadata: ImportedModelObjectMetadata | undefined,
  root: THREE.Object3D,
): boolean {
  return object === root || Boolean(hierarchyNode) || Boolean(metadata)
}

/**
 * 构建插入重绑定运行时上下文。
 *
 * @param input 重绑定输入。
 * @returns 运行时上下文。
 */
function createRebindRuntimeContext(
  input: RebindInsertedImportedModelAssemblySelectionMetadataInput,
): RebindRuntimeContext {
  const objectByUuid = collectObjectByUuid(input.root)
  const insertedHierarchyByObjectId = new Map<string, GlbHierarchyNode>()
  collectHierarchyByObjectId(input.insertedHierarchy, insertedHierarchyByObjectId)

  const sourceObjectIdToInsertedHierarchy = new Map<string, GlbHierarchyNode>()
  collectSourceObjectIdToInsertedHierarchy(
    input.sourceHierarchy,
    input.insertedHierarchy,
    sourceObjectIdToInsertedHierarchy,
  )

  const rootAssemblyNodeId = normalizeString(input.insertedHierarchy.objectId) || input.root.uuid
  const rootProxyObject = objectByUuid.get(rootAssemblyNodeId) ?? input.root
  const newAssemblyNodeIdByOldNodeId = buildAssemblyNodeRebindMap(input.root, {
    objectByUuid,
    insertedHierarchyByObjectId,
    rootAssemblyNodeId,
  })

  return {
    objectByUuid,
    insertedHierarchyByObjectId,
    sourceObjectIdToInsertedHierarchy,
    newAssemblyNodeIdByOldNodeId,
    rootAssemblyNodeId,
    rootProxyObject,
    sceneFileInfo: input.sceneFileInfo,
    insertedPartUuid: normalizeString(input.insertedPartUuid),
  }
}

/**
 * 将已插入当前装配体的导入模型重新绑定到当前装配体选择语义。
 *
 * 设计说明：
 * 1. “插入零部件”复用独立加载结果，普通零件不会天然携带 assemblyNodeId；
 * 2. Mate、基准引用、移动组件和 STEP 导出都依赖 importedModel metadata 中的实例级选择目标；
 * 3. 因此插入提交必须在装配体边界统一重写 metadata、selectionIndex 和 sceneBindings，
 *    避免 UI 层或 Mate 控制器用局部 fallback 吞掉根因。
 *
 * @param input 重绑定输入。
 * @returns 重绑定统计结果。
 */
export function rebindInsertedImportedModelAssemblySelectionMetadata(
  input: RebindInsertedImportedModelAssemblySelectionMetadataInput,
): RebindInsertedImportedModelAssemblySelectionMetadataResult {
  const context = createRebindRuntimeContext(input)
  let reboundObjectCount = 0
  let selectionBindingCount = 0
  let sceneBindingCount = 0

  const visitObject = (
    object: THREE.Object3D,
    inheritedSelectionContext: ReboundSelectionContext | null,
  ): void => {
    const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(object)
    const hierarchyNode = resolveInsertedHierarchyNode(object, context)
    const selectionContext = resolveReboundSelectionContext({
      object,
      metadata,
      hierarchyNode,
      inheritedSelectionContext,
      context,
    })

    if (!shouldRebindObject(object, hierarchyNode, metadata, input.root)) {
      object.children.forEach((child) => visitObject(child, selectionContext))
      return
    }

    const selectionTarget = createReboundSelectionTarget({
      metadata,
      assemblyNodeId: selectionContext.assemblyNodeId,
      partUuid: selectionContext.partUuid,
    })
    const bodyBindingSelectionTargetsById = rebindBodyBindingSelectionTargetsById(
      metadata,
      selectionContext.assemblyNodeId,
      selectionContext.partUuid,
      context,
    )

    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: context.sceneFileInfo.fileId,
      ...(hierarchyNode
        ? { hierarchy: createHierarchyMetadata(hierarchyNode, context.sceneFileInfo) }
        : {}),
      selectable: hierarchyNode?.selectable ?? metadata?.selectable,
      assemblyNodeId: selectionContext.assemblyNodeId,
      ...(selectionContext.partUuid ? { partFileUuid: selectionContext.partUuid } : {}),
      selectionProxyObjectId: selectionContext.proxyObject.uuid,
      selectionTarget,
      bodyBindingSelectionTargetsById,
      fileInfo: context.sceneFileInfo,
    })
    reboundObjectCount += 1

    input.selectionIndex.byObject3DUuid[object.uuid] = selectionTarget
    selectionBindingCount += 1

    if (appendSceneBinding(input.sceneBindings, selectionContext.assemblyNodeId, selectionContext.proxyObject)) {
      sceneBindingCount += 1
    }

    object.children.forEach((child) => visitObject(child, selectionContext))
  }

  visitObject(input.root, null)

  return {
    reboundObjectCount,
    selectionBindingCount,
    sceneBindingCount,
  }
}

//------ 装配体复制体选择元数据重绑定 开始------
/**
 * 将来源对象 ID 改写为克隆对象 ID。
 *
 * @param objectId 候选对象 ID。
 * @param clonedObjectIdBySourceObjectId 源对象 ID 到克隆对象 ID 的映射。
 * @returns 克隆对象 ID；无法命中映射时保持原值。
 */
function resolveClonedObjectId(
  objectId: string | null | undefined,
  clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>,
): string | null | undefined {
  if (!objectId) {
    return objectId
  }
  return clonedObjectIdBySourceObjectId.get(objectId) ?? objectId
}

/**
 * 改写高亮目标中的 Object3D 引用。
 *
 * @param targets 来源高亮目标。
 * @param clonedObjectIdBySourceObjectId 源对象 ID 到克隆对象 ID 的映射。
 * @returns 改写后的高亮目标。
 */
/**
 * 将可选的源对象 ID 改写到当前克隆实例 ID。
 *
 * @param objectId 源对象或语义节点 ID。
 * @param fallbackObjectId 无法命中克隆映射时使用的当前实例 ID。
 * @param clonedObjectIdBySourceObjectId 源对象 ID 到克隆对象 ID 的映射。
 * @returns 当前克隆实例内可用的对象 ID。
 */
function rewriteClonedOptionalObjectId(
  objectId: string | null | undefined,
  fallbackObjectId: string,
  clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>,
): string | undefined {
  const normalizedObjectId = normalizeString(objectId)
  if (!normalizedObjectId) {
    return undefined
  }

  return clonedObjectIdBySourceObjectId.get(normalizedObjectId)
    ?? normalizeString(fallbackObjectId)
    ?? normalizedObjectId
}

function rewriteClonedHighlightTargets(
  targets: NonNullable<ImportedModelObjectHierarchyMetadata['meta']>['highlightTargets'],
  clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>,
): NonNullable<ImportedModelObjectHierarchyMetadata['meta']>['highlightTargets'] {
  if (!Array.isArray(targets)) {
    return undefined
  }
  return targets.map((target) => ({
    objectId: resolveClonedObjectId(target.objectId, clonedObjectIdBySourceObjectId) ?? target.objectId,
    primitiveIndices: target.primitiveIndices ? [...target.primitiveIndices] : undefined,
  }))
}

/**
 * 改写 body 高亮目标中的 Object3D 引用。
 *
 * @param targetsByBodyId 来源 body 高亮目标表。
 * @param clonedObjectIdBySourceObjectId 源对象 ID 到克隆对象 ID 的映射。
 * @returns 改写后的 body 高亮目标表。
 */
function rewriteClonedBodyHighlightTargets(
  targetsByBodyId: NonNullable<ImportedModelObjectHierarchyMetadata['meta']>['bodyHighlightTargetsById'],
  clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>,
): NonNullable<ImportedModelObjectHierarchyMetadata['meta']>['bodyHighlightTargetsById'] {
  if (!targetsByBodyId) {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(targetsByBodyId).map(([bodyId, targets]) => [
      bodyId,
      rewriteClonedHighlightTargets(targets, clonedObjectIdBySourceObjectId) ?? [],
    ]),
  )
}

/**
 * 重建克隆对象 hierarchy metadata。
 *
 * @param hierarchy 来源 hierarchy metadata。
 * @param clonedRootObjectId 克隆根 occurrence ID。
 * @param clonedObjectIdBySourceObjectId 源对象 ID 到克隆对象 ID 的映射。
 * @returns 改写后的 hierarchy metadata。
 */
function rebuildClonedImportedHierarchyMetadata(
  hierarchy: ImportedModelObjectHierarchyMetadata | undefined,
  fallbackSelectionObjectId: string,
  clonedObjectIdBySourceObjectId: ReadonlyMap<string, string>,
): ImportedModelObjectHierarchyMetadata | undefined {
  if (!hierarchy) {
    return undefined
  }

  const meta = hierarchy.meta
  return {
    ...hierarchy,
    parentObjectId: resolveClonedObjectId(hierarchy.parentObjectId, clonedObjectIdBySourceObjectId),
    meta: meta
      ? {
          ...meta,
          assemblyNodeId: rewriteClonedOptionalObjectId(
            meta.assemblyNodeId,
            fallbackSelectionObjectId,
            clonedObjectIdBySourceObjectId,
          ) ?? meta.assemblyNodeId,
          selectionObjectId: meta.selectionObjectId
            ? rewriteClonedOptionalObjectId(
                meta.selectionObjectId,
                fallbackSelectionObjectId,
                clonedObjectIdBySourceObjectId,
              )
            : meta.selectionObjectId,
          highlightTargets: rewriteClonedHighlightTargets(meta.highlightTargets, clonedObjectIdBySourceObjectId),
          bodyHighlightTargetsById: rewriteClonedBodyHighlightTargets(
            meta.bodyHighlightTargetsById,
            clonedObjectIdBySourceObjectId,
          ),
        }
      : meta,
  }
}

/**
 * 解析克隆对象的零件 UUID。
 *
 * @param metadata 当前对象 metadata。
 * @param fallbackPartUuid 兜底零件或子装配 UUID。
 * @returns 零件 UUID；缺失时返回空字符串。
 */
function resolveClonedPartUuid(
  metadata: ImportedModelObjectMetadata | undefined,
  fallbackPartUuid: string,
): string {
  return normalizeString(metadata?.selectionTarget?.partUuid)
    || normalizeString(metadata?.partFileUuid)
    || fallbackPartUuid
}

/**
 * 创建克隆对象选择目标。
 *
 * @param metadata 当前对象 metadata。
 * @param clonedRootObjectId 克隆根 occurrence ID。
 * @param fallbackPartUuid 兜底零件或子装配 UUID。
 * @returns 可写入 metadata 与 selectionIndex 的选择目标。
 */
/**
 * 将源 assemblyNodeId 解析到当前克隆对象树内。
 *
 * @param value 源 assemblyNodeId 或代理对象 ID。
 * @param context 克隆重绑定上下文。
 * @returns 克隆对象树内可用的 Object3D uuid；无法解析时返回空字符串。
 */
function resolveMappedClonedObjectId(
  value: unknown,
  context: ClonedRebindRuntimeContext,
): string {
  const normalizedValue = normalizeString(value)
  if (!normalizedValue) {
    return ''
  }
  return normalizeString(
    resolveClonedObjectId(normalizedValue, context.clonedObjectIdBySourceObjectId),
  )
}

/**
 * 解析当前克隆对象应归属的 assemblyNodeId。
 *
 * @param object 当前克隆对象。
 * @param metadata 当前克隆对象携带的旧 metadata。
 * @param context 克隆重绑定上下文。
 * @returns 当前克隆 occurrence 内的 assemblyNodeId。
 */
function resolveClonedAssemblyNodeId(
  object: THREE.Object3D,
  metadata: ImportedModelObjectMetadata | undefined,
  context: ClonedRebindRuntimeContext,
): string {
  const mappedAssemblyNodeId = resolveMappedClonedObjectId(resolveOriginalAssemblyNodeId(metadata), context)
  if (mappedAssemblyNodeId && context.objectByUuid.has(mappedAssemblyNodeId)) {
    return mappedAssemblyNodeId
  }

  const mappedProxyObjectId = resolveMappedClonedObjectId(metadata?.selectionProxyObjectId, context)
  if (mappedProxyObjectId && context.objectByUuid.has(mappedProxyObjectId)) {
    return mappedProxyObjectId
  }

  if (object === context.rootProxyObject) {
    return context.clonedRootObjectId
  }

  return object.uuid
}

/**
 * 解析当前克隆对象应写入的选择代理对象。
 *
 * @param metadata 当前克隆对象携带的旧 metadata。
 * @param assemblyNodeId 当前 selection context 的 assemblyNodeId。
 * @param context 克隆重绑定上下文。
 * @returns 当前克隆 occurrence 内的选择代理 Object3D。
 */
function resolveClonedSelectionProxyObject(
  metadata: ImportedModelObjectMetadata | undefined,
  assemblyNodeId: string,
  context: ClonedRebindRuntimeContext,
): THREE.Object3D {
  const mappedProxyObjectId = resolveMappedClonedObjectId(metadata?.selectionProxyObjectId, context)
  return context.objectByUuid.get(mappedProxyObjectId)
    ?? context.objectByUuid.get(assemblyNodeId)
    ?? context.rootProxyObject
}

/**
 * 创建克隆对象自身的 selection context。
 *
 * @param object 当前克隆对象。
 * @param metadata 当前克隆对象 metadata。
 * @param context 克隆重绑定上下文。
 * @returns 当前组件边界的 selection context。
 */
function resolveOwnClonedSelectionContext(
  object: THREE.Object3D,
  metadata: ImportedModelObjectMetadata | undefined,
  context: ClonedRebindRuntimeContext,
): ReboundSelectionContext {
  const assemblyNodeId = resolveClonedAssemblyNodeId(object, metadata, context)
  const proxyObject = resolveClonedSelectionProxyObject(metadata, assemblyNodeId, context)
  const partUuid = resolveClonedPartUuid(metadata, context.insertedPartUuid)
  return {
    assemblyNodeId,
    partUuid,
    proxyObject,
  }
}

/**
 * 解析克隆对象最终应继承或开启的 selection context。
 *
 * @param input 解析输入。
 * @returns 当前对象最终使用的 selection context。
 */
function resolveClonedSelectionContext(input: {
  readonly object: THREE.Object3D
  readonly metadata: ImportedModelObjectMetadata | undefined
  readonly inheritedSelectionContext: ReboundSelectionContext | null
  readonly context: ClonedRebindRuntimeContext
}): ReboundSelectionContext {
  if (
    isImportedHierarchyMetadataSelectionBoundary(input.metadata?.hierarchy)
    || !input.inheritedSelectionContext
  ) {
    return resolveOwnClonedSelectionContext(
      input.object,
      input.metadata,
      input.context,
    )
  }

  return input.inheritedSelectionContext
}

function createClonedSelectionTarget(
  input: {
    readonly metadata: ImportedModelObjectMetadata | undefined
    readonly assemblyNodeId: string
    readonly partUuid: string
  },
): SelectionTarget {
  return {
    assemblyNodeId: input.assemblyNodeId,
    ...(input.partUuid ? { partUuid: input.partUuid } : {}),
    ...cloneSelectionTargetSubEntityFields(input.metadata?.selectionTarget),
  }
}

/**
 * 重写克隆对象 bodyBindingId 级选择目标。
 *
 * @param metadata 当前对象 metadata。
 * @param clonedRootObjectId 克隆根 occurrence ID。
 * @param fallbackPartUuid 兜底零件或子装配 UUID。
 * @returns 改写后的 body 选择目标表。
 */
function rebindClonedBodyBindingSelectionTargetsById(
  metadata: ImportedModelObjectMetadata | undefined,
  fallbackAssemblyNodeId: string,
  fallbackPartUuid: string,
  context: ClonedRebindRuntimeContext,
): Record<string, ImportedModelSelectionTargetMetadata> | undefined {
  const sourceTargets = metadata?.bodyBindingSelectionTargetsById
  if (!sourceTargets) {
    return undefined
  }

  const reboundTargets: Record<string, ImportedModelSelectionTargetMetadata> = {}
  Object.entries(sourceTargets).forEach(([bodyBindingId, target]) => {
    const normalizedBodyBindingId = normalizeString(bodyBindingId)
    if (!normalizedBodyBindingId) {
      return
    }

    const mappedAssemblyNodeId = rewriteClonedOptionalObjectId(
      target?.assemblyNodeId,
      fallbackAssemblyNodeId,
      context.clonedObjectIdBySourceObjectId,
    )
    const assemblyNodeId = mappedAssemblyNodeId && context.objectByUuid.has(mappedAssemblyNodeId)
      ? mappedAssemblyNodeId
      : fallbackAssemblyNodeId
    const partUuid = normalizeString(target?.partUuid) || fallbackPartUuid
    reboundTargets[normalizedBodyBindingId] = {
      assemblyNodeId,
      ...(partUuid ? { partUuid } : {}),
      ...cloneSelectionTargetSubEntityFields(target),
    }
  })

  return Object.keys(reboundTargets).length > 0 ? reboundTargets : undefined
}

/**
 * 将克隆 occurrence 的 imported-model metadata 绑定到克隆根。
 *
 * 设计说明：
 * 1. 引用复制与线性阵列都会直接 clone 当前 Object3D 树；
 * 2. 克隆树里的 `selectionProxyObjectId`、`assemblyNodeId` 和高亮目标默认仍指向源对象；
 * 3. 若不在 cad-core 边界统一改写，点/线/面二阶段拾取会回到源件，复制体无法作为独立 occurrence 选择。
 *
 * @param input 重绑定输入。
 * @returns 重绑定统计结果。
 */
export function rebindClonedImportedModelAssemblySelectionMetadata(
  input: RebindClonedImportedModelAssemblySelectionMetadataInput,
): RebindClonedImportedModelAssemblySelectionMetadataResult {
  const objectByUuid = collectObjectByUuid(input.root)
  const clonedRootObjectId = normalizeString(input.clonedRootObjectId) || input.root.uuid
  const context: ClonedRebindRuntimeContext = {
    objectByUuid,
    clonedObjectIdBySourceObjectId: input.clonedObjectIdBySourceObjectId,
    clonedRootObjectId,
    rootProxyObject: objectByUuid.get(clonedRootObjectId) ?? input.root,
    insertedPartUuid: normalizeString(input.insertedPartUuid),
  }
  let reboundObjectCount = 0
  let selectionBindingCount = 0
  let sceneBindingCount = 0

  const visitObject = (
    object: THREE.Object3D,
    inheritedSelectionContext: ReboundSelectionContext | null,
  ): void => {
    const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
    const selectionContext = metadata || object === input.root
      ? resolveClonedSelectionContext({
          object,
          metadata,
          inheritedSelectionContext,
          context,
        })
      : inheritedSelectionContext

    if (!metadata && object !== input.root) {
      object.children.forEach((child) => visitObject(child, selectionContext))
      return
    }

    const fallbackSelectionContext = selectionContext ?? resolveOwnClonedSelectionContext(
      object,
      metadata,
      context,
    )
    const selectionTarget = createClonedSelectionTarget({
      metadata,
      assemblyNodeId: fallbackSelectionContext.assemblyNodeId,
      partUuid: fallbackSelectionContext.partUuid,
    })
    const hierarchy = rebuildClonedImportedHierarchyMetadata(
      metadata?.hierarchy,
      fallbackSelectionContext.assemblyNodeId,
      input.clonedObjectIdBySourceObjectId,
    )
    const bodyBindingSelectionTargetsById = rebindClonedBodyBindingSelectionTargetsById(
      metadata,
      fallbackSelectionContext.assemblyNodeId,
      fallbackSelectionContext.partUuid,
      context,
    )

    ImportedModelObjectMetadataService.writeMetadata(object, {
      ...(input.sceneFileInfo ? { fileId: input.sceneFileInfo.fileId } : {}),
      ...(hierarchy ? { hierarchy } : {}),
      selectable: metadata?.selectable,
      assemblyNodeId: fallbackSelectionContext.assemblyNodeId,
      ...(fallbackSelectionContext.partUuid ? { partFileUuid: fallbackSelectionContext.partUuid } : {}),
      selectionProxyObjectId: fallbackSelectionContext.proxyObject.uuid,
      selectionTarget,
      bodyBindingSelectionTargetsById,
      ...(input.sceneFileInfo ? { fileInfo: input.sceneFileInfo } : {}),
    })
    reboundObjectCount += 1

    if (input.selectionIndex) {
      input.selectionIndex.byObject3DUuid[object.uuid] = selectionTarget
      selectionBindingCount += 1
    }

    if (
      input.sceneBindings
      && appendSceneBinding(
        input.sceneBindings,
        fallbackSelectionContext.assemblyNodeId,
        fallbackSelectionContext.proxyObject,
      )
    ) {
      sceneBindingCount += 1
    }

    object.children.forEach((child) => visitObject(child, fallbackSelectionContext))
  }

  visitObject(input.root, null)

  return {
    reboundObjectCount,
    selectionBindingCount,
    sceneBindingCount,
  }
}
//------ 装配体复制体选择元数据重绑定 结束------
